/**
 * Unified Results Modal
 * 
 * Single, clean modal architecture that replaces all the fragmented modal patterns.
 * Uses NavigationService for consistent routing and state management.
 */

import '../styles/results.css';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaTimes, FaShare, FaDownload, FaArrowLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import ModalPortal from './ModalPortal';
import ModalErrorBoundary from './ModalErrorBoundary';
import JobsListView from '../results/JobsListView';
import OrdersListView from '../results/OrdersListView';
import SingleOrderResults from '../results/SingleOrderResults';
import navigationService from '../../../services/NavigationService';
import { useJobsData } from '../../../hooks/useJobsData';
import { useOrdersWithJobsData } from '../../../hooks/useOrdersWithJobsData';
import { useModalAccessibility } from '../../../hooks/useAccessibility';
import IntegratedSidebar from '../sidebar/IntegratedSidebar';
import { useModalLayer } from '../../../hooks/useModalLayer';
import { MODAL_LAYERS } from './ModalLayerManager';
import { resolveEvidenceMedia } from '../../../services/mediaResolver';

// OLD GLOBAL STATE REMOVED - Now using ModalLayerManager
// The ModalLayerManager handles all body scroll locking and modal coordination

const UnifiedResultsModal = ({
  isOpen,
  onClose,
  onRequestEvidenceModal = null, // NEW: Callback to request opening Evidence modal (handled by parent)
  // Context configuration
  context = {},
  // Filter configuration
  filters = {},
  // UI configuration
  ui = {},
  // Entry point tracking
  origin = 'unknown',
  // Portal configuration
  disablePortal = false, // New prop to disable internal portal
  // Display mode configuration
  slideMode = false, // New prop for sliding behavior
  // Context update callback for minimized state
  onContextUpdate = null, // Callback to notify parent when context should be updated
  // Filter update callback
  onUpdateFilters = null, // NEW: Callback to update filters from child components
  // Modal layer management
  disableModalLayerManager = false, // Disable ModalLayerManager when used as child component
  // Navigation isolation
  disableGlobalNavigation = false // Disable global NavigationService calls when used as child component
}) => {
  // Debug log component lifecycle and filters
  useEffect(() => {
    console.log('🔄 [UnifiedResultsModal] MOUNTED with filters:', filters);
    console.log('🔄 [UnifiedResultsModal] isOpen:', isOpen, 'origin:', origin);
    
    return () => {
      console.log('🔄 [UnifiedResultsModal] UNMOUNTING');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount/unmount
  
  // Debug log when filters prop changes
  useEffect(() => {
    console.log('🔄 [UnifiedResultsModal] Filters prop changed:', filters);
  }, [filters]);
  // Modal transition state
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  // Slide mode state
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Minimize state
  const [isMinimized, setIsMinimized] = useState(false);
  
  // ModalLayerManager integration
  // Always call the hook, but disable its functionality when used as child component
  const {
    modalId,
    zIndex,
    updateModal
  } = useModalLayer('results', disableModalLayerManager ? false : isOpen, {
    layer: isMinimized ? MODAL_LAYERS.MINIMIZED : MODAL_LAYERS.RESULTS,
    isMinimized,
    disabled: disableModalLayerManager, // Pass disabled flag to the hook
    onCloseRequest: (reason) => {
      console.log('🚪 Results modal close requested:', reason);
      onClose();
    }
  });

  // Update ModalLayerManager when minimize state changes
  useEffect(() => {
    if (isOpen && updateModal) {
      updateModal({
        layer: isMinimized ? MODAL_LAYERS.MINIMIZED : MODAL_LAYERS.RESULTS,
        isMinimized
      });
      console.log('🔄 Updated results modal layer:', {
        modalId,
        isMinimized,
        layer: isMinimized ? 'MINIMIZED (100)' : 'RESULTS (15000)'
      });
    }
  }, [isMinimized, isOpen, updateModal, modalId]);
  
  // ModalLayerManager handles body class and scroll position automatically
  
  // Companion modal ref for positioning
  const companionModalRef = useRef(null);
  
  // Companion modal state (preserved for future use)
  const [isCompanionExpanded, setIsCompanionExpanded] = useState(false);
  
  // Integrated sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarContext, setSidebarContext] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Note: Companion closing when results expands is now handled directly in the expand button click handler
  
  // Note: Results modal collapsing when companion opens is now handled directly in the companion toggle callback
  // Extract configuration
  const {
    evidenceId,
    casefileId, // Extract casefileId from context for evidence data
    orderId: initialOrderId,
    jobId: initialJobId,
    evidenceName,
    casefileName,
    currentUser,
    orderName
  } = context;

  const {
    pageSize = 20
  } = ui;

  // Navigation state from NavigationService
  const [navigationState, setNavigationState] = useState(navigationService.getCurrentState());
  
  // Track navigation source to determine context
  const [, setNavigationSource] = useState(null);
  
  // Track if we're in similar job navigation mode (persists across back navigation)
  const [isInSimilarJobMode, setIsInSimilarJobMode] = useState(false);
  
  // Track original job for "Back to Job A" functionality
  const [originalJobContext, setOriginalJobContext] = useState(null);
  
  // Track job navigation history for step-by-step back navigation
  const [jobNavigationHistory, setJobNavigationHistory] = useState([]);

  // View mode toggle - 'jobs' for jobs-only view, 'orders' for original orders view
  const [viewMode, setViewMode] = useState('jobs');

  // Shared video state for JobsListView (declared early to avoid initialization errors)
  const [sharedVideoUrl, setSharedVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);

  // Modal data management - reactive to navigation state
  // Memoize filters to prevent infinite re-renders
  // Create a stable stringified version of filters for comparison
  const filtersString = JSON.stringify(filters);
  const memoizedFilters = useMemo(() => {
    const result = {
      ...filters,
      // Add navigation-based filters (but don't override existing orderIds)
      ...(navigationState.orderId && !filters.orderIds && { orderIds: [navigationState.orderId] })
    };
    console.log('🔄 [UnifiedResultsModal] Memoized filters computed:', result);
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersString, navigationState.orderId]); // Use stringified version for stable comparison

  // Optimize page size for slide mode - smaller initial load for better performance
  const optimizedPageSize = slideMode ? Math.min(pageSize, 15) : pageSize;

  // Jobs-only data hook
  const jobsData = useJobsData({
    filters: memoizedFilters,
    pageSize: optimizedPageSize,
    modalId: `unified_modal_${origin}_${navigationState.orderId || 'all'}`,
    enableRealTime: true, // Always enable real-time to stay in sync with History tab
    userScope: 'all' // Show all users' jobs for global discovery
  });

  // Original orders-based data hook
  const modalData = useOrdersWithJobsData({
    filters: memoizedFilters,
    pageSize: optimizedPageSize,
    modalId: `unified_modal_${origin}_${navigationState.orderId || 'all'}`,
    accordionMode: true,
    enableRealTimeJobs: true, // Always enable real-time to stay in sync
    enableResultsParsing: true,
    userScope: 'all' // Show all users' orders and jobs for global discovery
  });

  // Trigger data reload when modal opens or filters change
  const filtersStringified = JSON.stringify(memoizedFilters);
  const prevFiltersRef = useRef(filtersStringified);
  const prevIsOpenRef = useRef(isOpen);
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    const filtersChanged = prevFiltersRef.current !== filtersStringified;
    const isOpenChanged = prevIsOpenRef.current !== isOpen;
    const justOpened = !prevIsOpenRef.current && isOpen;
    const wasExternalUpdate = filtersChanged && filters.evidenceIds && filters.evidenceIds.length > 0;
    
    console.log('🔄 [UnifiedResultsModal] Effect triggered:', {
      isOpen,
      isOpenChanged,
      justOpened,
      hasLoadOrders: !!modalData.loadOrders,
      filters: memoizedFilters,
      currentMode: navigationState.mode,
      filtersChanged,
      wasExternalUpdate,
      hasLoadedRef: hasLoadedRef.current
    });
    
    // Only load data if:
    // 1. Modal just opened and we haven't loaded yet, OR
    // 2. Filters have actually changed (and modal is open)
    const shouldLoad = modalData.loadOrders && ((justOpened && !hasLoadedRef.current) || (isOpen && filtersChanged && hasLoadedRef.current));
    
    if (shouldLoad) {
      console.log('🔄 [UnifiedResultsModal] TRIGGERING LOAD with filters:', memoizedFilters);
      modalData.loadOrders(true); // Reset pagination and load fresh data
      hasLoadedRef.current = true;
      
      // If filters changed externally (evidence filter added from outside), switch to list view
      if (wasExternalUpdate && navigationState.mode !== 'list') {
        console.log('🔄 [UnifiedResultsModal] External filter change detected - switching to list view');
        if (!disableGlobalNavigation) {
          navigationService.navigateToList({ 
            updateUrl: true, 
            source: 'external_filter_change' 
          });
        }
      }
    }
    
    // Reset hasLoaded when modal closes
    if (!isOpen && prevIsOpenRef.current) {
      console.log('🔄 [UnifiedResultsModal] Modal closed - resetting load flag');
      hasLoadedRef.current = false;
    }
    
    prevFiltersRef.current = filtersStringified;
    prevIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filtersStringified]); // Watch stringified filters for changes
  // Note: modalData.loadOrders intentionally not in deps to avoid infinite loops

  // Choose which data to use based on view mode
  const currentData = viewMode === 'jobs' ? {
    loading: jobsData.loading,
    error: jobsData.error,
    hasMore: jobsData.hasMore,
    loadMore: jobsData.loadMore,
    refresh: jobsData.refresh,
    stats: jobsData.stats
  } : {
    loading: modalData.loading,
    error: modalData.error,
    hasMore: modalData.hasMore,
    loadMore: modalData.loadMore,
    refresh: modalData.refresh,
    stats: modalData.stats
  };

  // Accessibility
  const {
    modalRef,
    announcements,
    announce,
    getModalProps,
    getInteractiveProps
  } = useModalAccessibility(isOpen, `unified-results-modal-${origin}`);

  // Detect mobile viewport changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update sidebar context based on current view
  useEffect(() => {
    // Check ownership: Get evidence owner from jobs data or context
    const firstJob = jobsData.jobs?.[0];
    const evidenceOwnerId = firstJob?.userId || firstJob?.createdBy || currentUser?.uid; // Assume current user is owner if no jobs yet
    const isOwner = currentUser && evidenceOwnerId && currentUser.uid === evidenceOwnerId;
    
    // Get evidence data for owner controls - use context data if no jobs yet
    const evidenceData = evidenceId ? {
      id: evidenceId,
      fileName: evidenceName || 'Evidence',
      casefileId: casefileId || firstJob?.casefileId,
      contentType: firstJob?.contentType,
      fileSize: firstJob?.fileSize,
      createdAt: firstJob?.createdAt,
      createdBy: evidenceOwnerId,
      userId: evidenceOwnerId,
      isPublic: firstJob?.isPublic,
      downloadURL: firstJob?.downloadURL || sharedVideoUrl
    } : null;

    // Default to job list view
    const context = {
      type: 'job-list',
      title: 'Other Jobs',
      data: {
        evidence: { id: evidenceId, name: evidenceName || 'Evidence' },
        jobs: jobsData.jobs || [],
        currentJobId: navigationState.jobId || null,
        onJobSelect: (jobId) => {
          navigationService.navigateToJob(jobId, navigationState.orderId, {
            updateUrl: true,
            source: 'sidebar_job_click'
          });
        }
      },
      notificationCount: 0,
      actions: []
    };

    // CRITICAL: Check if we should show owner controls or evidence info
    // Show when: viewing main results list and have evidence data
    if (navigationState.mode === 'list' && evidenceData) {
      if (isOwner) {
        console.log('🎛️ Showing owner controls for evidence:', evidenceId, 'Owner:', currentUser.uid);
        context.type = 'owner-controls';
        context.title = 'Owner Controls';
      } else {
        console.log('📋 Showing evidence info for non-owner:', evidenceId);
        context.type = 'owner-controls'; // Same component, different rendering based on ownership
        context.title = 'Evidence Info';
      }
      
      context.data = {
        evidence: evidenceData,
        currentUser: currentUser,
        casefileId: casefileId || firstJob?.casefileId,
        onUpdateSuccess: (updatedData) => {
          console.log('✅ Evidence updated:', updatedData);
          // Refresh jobs data to get updated evidence info
          if (jobsData.refresh) {
            jobsData.refresh();
          }
        },
        // Include jobs list below owner controls
        jobs: jobsData.jobs || [],
        currentJobId: navigationState.jobId || null,
        onJobSelect: (jobId) => {
          navigationService.navigateToJob(jobId, navigationState.orderId, {
            updateUrl: true,
            source: 'sidebar_job_click'
          });
        }
      };
    } else if (navigationState.mode === 'order' || navigationState.mode === 'orders') {
      // Viewing orders - show evidence details
      context.type = 'evidence-details';
      context.title = 'Evidence Details';
      context.data = {
        evidence: { id: evidenceId, name: evidenceName || 'Evidence' },
        relatedItems: [
          { type: 'job', name: 'Analysis Jobs', count: jobsData.jobs?.length || 0 },
          { type: 'order', name: 'Processing Orders', count: modalData.orders?.length || 0 }
        ]
      };
    } else if (isInSimilarJobMode && jobNavigationHistory.length > 0) {
      // Similar job navigation - show navigation history
      context.type = 'similar-jobs';
      context.title = 'Navigation History';
      context.data = {
        navigationPath: jobNavigationHistory.map(item => ({
          label: item.fromJob ? `Job ${item.fromJob}` : 'Original',
          jobId: item.jobId
        })),
        jobs: jobsData.jobs || [],
        currentJobId: navigationState.jobId
      };
    }

    setSidebarContext(context);
  }, [
    navigationState,
    evidenceId,
    evidenceName,
    casefileId,
    jobsData.jobs,
    modalData.orders,
    isInSimilarJobMode,
    jobNavigationHistory,
    currentUser,
    sharedVideoUrl
  ]);

  // Handle modal opening/closing transitions (ModalLayerManager handles body scroll)
  useEffect(() => {
    if (isOpen) {
      // Start transition
      setIsTransitioning(true);
      setIsVisible(true);
      
      // Complete transition after animation
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    } else if (isVisible) {
      // Start closing transition
      setIsTransitioning(true);
      
      // Complete transition
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsTransitioning(false);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // ModalLayerManager handles cleanup automatically

  // Close handler
  const handleClose = useCallback(() => {
    // Reset navigation state
    navigationService.navigateToList({ 
      updateUrl: false, 
      source: 'modal_close' 
    });
    onClose();
  }, [onClose]);

  // Enhanced ESC key handling for full-screen modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isVisible) {
        event.preventDefault();
        event.stopPropagation();
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown, true);
      return () => document.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [isVisible, handleClose]);

  // Subscribe to navigation changes (unless disabled for child modals)
  useEffect(() => {
    if (disableGlobalNavigation) {
      console.log('🚫 Global navigation disabled for child modal - skipping NavigationService subscription');
      return;
    }
    
    let lastEventId = null; // Prevent duplicate processing
    
    const unsubscribe = navigationService.subscribe((event) => {
      // Simple deduplication based on event content
      const eventId = `${event.type}-${event.payload.jobId || event.payload.orderId}-${event.payload.source}`;
      if (eventId === lastEventId) {
        console.log('🔄 Skipping duplicate navigation event');
        return;
      }
      lastEventId = eventId;
      
      console.log('🎯 UnifiedResultsModal: Navigation event:', event);
      const newState = navigationService.getCurrentState();
      setNavigationState(newState);
      
      // Track navigation source for context awareness
      setNavigationSource(event.payload.source);
      
      // Track job navigation history and original context
      if (event.type === 'NAVIGATE_TO_JOB') {
        const previousJob = event.payload.previousState;
        const currentJob = { jobId: event.payload.jobId, orderId: event.payload.orderId };
        
        if (event.payload.source === 'similar_job_open_button') {
          console.log('🔍 Similar job navigation detected');
          console.log('🔍 Current originalJobContext:', originalJobContext?.jobId?.slice(-8) || 'none');
          console.log('🔍 Previous job:', previousJob?.jobId?.slice(-8) || 'none');
          console.log('🔍 Current job:', currentJob?.jobId?.slice(-8) || 'none');
          
          // Set original context ONLY on the very first similar job navigation
          // Use a ref to get the current value synchronously
          setOriginalJobContext(prevOriginal => {
            if (!prevOriginal && previousJob && previousJob.jobId) {
              console.log('🏠 FIRST TIME: Storing ORIGINAL job context (Job A):', previousJob.jobId.slice(-8));
              setIsInSimilarJobMode(true);
              return {
                jobId: previousJob.jobId,
                orderId: previousJob.orderId
              };
            } else if (prevOriginal) {
              console.log('🏠 PRESERVING original job context (Job A):', prevOriginal.jobId.slice(-8));
              setIsInSimilarJobMode(true);
              return prevOriginal; // Keep the existing original context
            }
            return prevOriginal;
          });
          
          // Add previous job to history stack (avoid duplicates)
          if (previousJob && previousJob.jobId) {
            setJobNavigationHistory(prev => {
              const newHistory = [...prev];
              const lastJob = newHistory[newHistory.length - 1];
              
              // Only add if it's different from the last job in history
              if (!lastJob || lastJob.jobId !== previousJob.jobId) {
                newHistory.push({
                  jobId: previousJob.jobId,
                  orderId: previousJob.orderId,
                  timestamp: Date.now()
                });
                console.log('📚 Added to job history:', previousJob.jobId.slice(-8));
                console.log('📚 Full history now:', newHistory.map(j => j.jobId.slice(-8)));
              } else {
                console.log('📚 Skipped duplicate in history:', previousJob.jobId.slice(-8));
              }
              
              return newHistory;
            });
          }
        } else if (event.payload.source === 'back_to_previous_job') {
          // Stay in similar job mode, just update history
          console.log('⬅️ Back to previous job - staying in similar job mode');
          setJobNavigationHistory(prev => {
            const newHistory = [...prev];
            newHistory.pop();
            console.log('📚 Removed from job history, remaining:', newHistory.length);
            return newHistory;
          });
        } else if (event.payload.source === 'back_to_original_job') {
          // Returning to original job - exit similar job mode
          console.log('🏠 Returning to original job - exiting similar job mode');
          setIsInSimilarJobMode(false);
          setJobNavigationHistory([]);
          // Keep originalJobContext for potential future similar navigation
        }
      }
      
              // Clear all context when returning to list or direct navigation
        if (event.type === 'NAVIGATE_TO_LIST' || 
            (event.payload.source?.includes('modal_open') && event.payload.source !== 'similar_job_open_button')) {
          console.log('🧹 Clearing all job navigation context');
          setIsInSimilarJobMode(false);
          setOriginalJobContext(null);
          setJobNavigationHistory([]);
        }
        
        // Debug: Log current state after all updates
        console.log('🔍 After navigation - originalJobContext:', originalJobContext?.jobId?.slice(-8) || 'none');
        console.log('🔍 After navigation - isInSimilarJobMode:', isInSimilarJobMode);
      
      // Announce navigation changes for accessibility
      if (event.type === 'NAVIGATE_TO_ORDER') {
        announce(`Navigated to order ${event.payload.orderId}`);
      } else if (event.type === 'NAVIGATE_TO_JOB') {
        const isSimilarJob = event.payload.source === 'similar_job_open_button';
        announce(`Navigated to ${isSimilarJob ? 'similar' : ''} job ${event.payload.jobId}`);
      } else if (event.type === 'NAVIGATE_TO_LIST') {
        announce('Navigated to orders list');
      }
      
      // CRITICAL: Force modal data refresh when navigation changes
      if (event.type === 'NAVIGATE_TO_ORDER' || event.type === 'NAVIGATE_TO_JOB') {
        console.log('🔄 UnifiedResultsModal: Refreshing modal data for navigation');
        // The modalData hook should automatically refresh based on the new state
      }
    });

    return unsubscribe;
  }, [announce, isInSimilarJobMode, originalJobContext, disableGlobalNavigation]);

  // ModalLayerManager handles body class changes automatically


  // Track previous values to detect actual changes
  const prevEvidenceIdRef = useRef(evidenceId);
  const prevJobIdRef = useRef(navigationState.jobId);
  const prevOrderIdRef = useRef(navigationState.orderId);

  // Auto-restore when context changes (new evidence/results selected)
  // ModalLayerManager now handles modal interaction conflicts
  useEffect(() => {
    if (isMinimized) {
      console.log('🔍 Evidence context while minimized:', {
        evidenceChanged: evidenceId && evidenceId !== prevEvidenceIdRef.current,
        evidenceId: { old: prevEvidenceIdRef.current, new: evidenceId },
        isOpen,
        origin
      });
    }
    
    // Only restore if evidence actually changed to a different value while minimized
    // AND we're not in a context where an evidence modal is trying to open
    if (isMinimized && isOpen && evidenceId && evidenceId !== prevEvidenceIdRef.current) {
      // Check if there are any evidence modals currently registering/opening
      // If so, don't auto-restore as the user wants to open the evidence modal, not restore results
      const hasEvidenceModalOpening = window.modalLayerManager?.hasModalType?.('evidence') || false;
      
      if (hasEvidenceModalOpening) {
        console.log('🚫 Evidence modal is opening - skipping auto-restore', {
          old: prevEvidenceIdRef.current,
          new: evidenceId,
          origin,
          hasEvidenceModalOpening
        });
      } else {
        console.log('📈 Evidence changed while minimized - auto-restoring results modal', {
          old: prevEvidenceIdRef.current,
          new: evidenceId,
          origin
        });
        
        setTimeout(() => {
          setIsMinimized(false);
        }, 100);
      }
    }
    
    prevEvidenceIdRef.current = evidenceId;
  }, [evidenceId, isMinimized, isOpen, origin]);

  // Auto-restore when navigation changes (new job/order selected)
  useEffect(() => {
    // Only restore if job/order actually changed to a different value while minimized
    const jobChanged = navigationState.jobId && navigationState.jobId !== prevJobIdRef.current;
    const orderChanged = navigationState.orderId && navigationState.orderId !== prevOrderIdRef.current;
    
    if (isMinimized) {
      console.log('🔍 Navigation state while minimized:', {
        jobChanged,
        orderChanged,
        jobId: { old: prevJobIdRef.current, new: navigationState.jobId },
        orderId: { old: prevOrderIdRef.current, new: navigationState.orderId },
        isOpen,
        origin
      });
      
      if (jobChanged || orderChanged) {
        console.log('📈 Navigation changed while minimized - restoring modal');
        setIsMinimized(false);
      }
    }
    
    prevJobIdRef.current = navigationState.jobId;
    prevOrderIdRef.current = navigationState.orderId;
  }, [navigationState.jobId, navigationState.orderId, isMinimized, isOpen, origin]);

  // Notify parent about minimize state changes
  useEffect(() => {
    if (onContextUpdate && isMinimized) {
      // Register this modal as available for context updates
      onContextUpdate({ 
        type: 'minimized',
        updateContext: (newContext) => {
          // This would be called by external components to update the modal
          console.log('📝 Updating minimized modal context:', newContext);
          // The parent component should handle context updates
        }
      });
    }
  }, [isMinimized, onContextUpdate]);

  // Initialize navigation state when modal opens
  useEffect(() => {
    if (isOpen) {
      // For evidence modals, always start in list mode and expanded
      if (origin === 'evidence') {
        if (disableGlobalNavigation) {
          console.log('🚫 Evidence modal opening - global navigation disabled, skipping navigateToList call');
        } else {
          console.log('🎯 Evidence modal opening - forcing list mode and full screen');
          navigationService.navigateToList({ 
            updateUrl: true, 
            source: `modal_open_${origin}` 
          });
        }
        // Force full screen for evidence modals
        if (slideMode) {
          setIsExpanded(true);
        }
        return;
      }
      
      if (disableGlobalNavigation) {
        console.log('🚫 Global navigation disabled - skipping URL sync and navigation calls');
      } else {
        // Initialize URL synchronization for other modal types
        navigationService.initializeUrlSync();
        
        // Set initial navigation state based on props
        if (initialJobId && initialOrderId) {
          navigationService.navigateToJob(initialJobId, initialOrderId, { 
            updateUrl: true, 
            source: `modal_open_${origin}` 
          });
        } else if (initialOrderId) {
          navigationService.navigateToOrder(initialOrderId, null, { 
            updateUrl: true, 
            source: `modal_open_${origin}` 
          });
        } else {
          navigationService.navigateToList({ 
            updateUrl: true, 
            source: `modal_open_${origin}` 
          });
        }
      }
    }
  }, [isOpen, initialOrderId, initialJobId, origin, slideMode, disableGlobalNavigation]);

  // Navigation handlers
  const handleNavigateToOrder = useCallback((orderId, jobId = null) => {
    if (disableGlobalNavigation) {
      console.log('🚫 Global navigation disabled - skipping navigateToOrder');
      return;
    }
    navigationService.navigateToOrder(orderId, jobId, {
      updateUrl: true,
      source: 'unified_modal_navigation'
    });
  }, [disableGlobalNavigation]);

  const handleNavigateToJob = useCallback((orderId, jobId) => {
    if (disableGlobalNavigation) {
      console.log('🚫 Global navigation disabled - skipping navigateToJob');
      return;
    }
    
    // Handle both single jobId (jobs view) and orderId+jobId (orders view) parameters
    if (typeof orderId === 'string' && !jobId) {
      // Called from jobs view with just jobId
      jobId = orderId;
      const job = jobsData.getJobById(jobId);
      orderId = job?.orderId;
    }
    
    navigationService.navigateToJob(jobId, orderId, {
      updateUrl: true,
      source: 'unified_modal_job_selection'
    });
  }, [jobsData, disableGlobalNavigation]);

  const handleNavigateToList = useCallback(() => {
    if (disableGlobalNavigation) {
      console.log('🚫 Global navigation disabled - skipping navigateToList');
      return;
    }
    navigationService.navigateToList({
      updateUrl: true,
      source: 'unified_modal_back_button'
    });
  }, [disableGlobalNavigation]);

  // Dynamic breadcrumb generation based on navigation hierarchy
  const breadcrumbs = useMemo(() => {
    const { mode, orderId, jobId } = navigationState;
    const crumbs = [];
    
    // Level 1: Always start with root context
    if (casefileName) {
      crumbs.push({ 
        label: casefileName,
        displayLabel: casefileName, // Clean display
        contextLabel: 'Case', // Short prefix for context
        type: 'casefile',
        tooltip: `Casefile: ${casefileName}`,
        onClick: handleNavigateToList 
      });
    } else {
      crumbs.push({ 
        label: 'Analysis Results',
        displayLabel: 'Analysis Results',
        contextLabel: 'Results',
        type: 'root',
        tooltip: 'All analysis results',
        onClick: handleNavigateToList 
      });
    }
    
    // Level 2: Evidence context (if drilling into specific evidence)
    if (evidenceName && (mode === 'order' || mode === 'job' || origin === 'evidence')) {
      crumbs.push({ 
        label: evidenceName,
        displayLabel: evidenceName,
        contextLabel: 'Evidence',
        type: 'evidence',
        tooltip: `Evidence: ${evidenceName}`,
        onClick: handleNavigateToList 
      });
    }
    
    // Level 3: Order context (if viewing specific order)
    if (mode === 'order' && orderId) {
      const orderLabel = orderName || `Order ${orderId.slice(-8)}`;
      crumbs.push({ 
        label: orderLabel,
        displayLabel: orderLabel,
        contextLabel: 'Order',
        type: 'order',
        tooltip: `Analysis Order: ${orderLabel}`,
        onClick: () => handleNavigateToOrder(orderId)
      });
    }
    
    // Level 4: Job context (if viewing specific job)
    if (mode === 'job' && jobId) {
      const jobLabel = `Job #${jobId.slice(-8)}`;
      crumbs.push({ 
        label: jobLabel,
        displayLabel: jobLabel,
        contextLabel: 'Job',
        type: 'job',
        tooltip: `Analysis Job: ${jobLabel}`
        // No onClick for current job (end of hierarchy)
      });
    }
    
    return crumbs;
  }, [navigationState, casefileName, evidenceName, orderName, origin, handleNavigateToList, handleNavigateToOrder]);

  // Load shared video for jobs list
  useEffect(() => {
    // Only load video when in jobs list mode
    if (navigationState.mode !== 'list' || viewMode !== 'jobs') {
      return;
    }
    
    const loadSharedVideo = async () => {
      // Try to get evidence info from jobs first, fall back to context
      let targetEvidenceId = evidenceId;
      let targetCasefileId = casefileId;
      
      if (jobsData.jobs && jobsData.jobs.length > 0) {
        // Find first job with evidence info
        const firstJobWithEvidence = jobsData.jobs.find(job => job.evidenceId && job.casefileId);
        if (firstJobWithEvidence) {
          targetEvidenceId = firstJobWithEvidence.evidenceId;
          targetCasefileId = firstJobWithEvidence.casefileId;
        }
      }
      
      // If we still don't have evidence info, bail out
      if (!targetEvidenceId || !targetCasefileId) return;
      
      setVideoLoading(true);
      try {
        const media = await resolveEvidenceMedia({
          evidenceId: targetEvidenceId,
          casefileId: targetCasefileId
        });
        
        if (media?.url) {
          setSharedVideoUrl(media.url);
        }
      } catch (err) {
        console.error('Failed to load shared video:', err);
      } finally {
        setVideoLoading(false);
      }
    };
    
    loadSharedVideo();
  }, [jobsData.jobs, navigationState.mode, viewMode, evidenceId, casefileId]);

  // Render content based on navigation state
  const renderContent = () => {
    const { mode, orderId, jobId } = navigationState;
    
    switch (mode) {
      case 'job':
      case 'order':
        if (orderId) {
          // For single order/job view, render SingleOrderResults without additional wrapper
          return (
            <SingleOrderResults
              orderId={orderId}
              focusJobId={jobId}
              currentUser={currentUser}
              onNavigateToOrder={handleNavigateToOrder}
              onActiveTabChange={(tabId) => {
                console.log('🎯 Active tab changed:', tabId);
              }}
              onUpdateFilters={onUpdateFilters} // Pass filter update callback
              onClose={onClose}
              onRequestEvidenceModal={onRequestEvidenceModal} // Pass callback to parent
            />
          );
        }
        break;
        
      case 'list':
      default:
        // Render based on view mode
        if (viewMode === 'jobs') {
          // Build evidence data from context (always available) and supplement with job data if available
          const firstJob = jobsData.jobs?.[0];
          const evidenceData = evidenceId ? {
            id: evidenceId,
            fileName: evidenceName || 'Evidence',
            casefileId: casefileId || firstJob?.casefileId,
            downloadURL: sharedVideoUrl
          } : null;
          
          return (
            <JobsListView
              jobs={jobsData.jobs}
              resultsByJobId={jobsData.results}
              onOpenJob={handleNavigateToJob}
              hasMore={jobsData.hasMore}
              isLoading={jobsData.loading}
              onLoadMore={jobsData.loadMore}
              sharedVideoUrl={sharedVideoUrl}
              sharedVideoMetadata={{
                loading: videoLoading
              }}
              // Pass evidence and user data for Actions tab
              evidence={evidenceData}
              casefileId={casefileId || firstJob?.casefileId}
              casefileName={casefileName}
              currentUser={currentUser}
              onEvidenceUpdate={() => {
                // Refresh jobs data when evidence is updated
                jobsData.refresh?.();
              }}
            />
          );
        } else {
          return (
            <OrdersListView
              orders={modalData.orders}
              expandedOrders={modalData.expandedOrders}
              jobsByOrderId={modalData.rawData.jobs.jobsByOrderId}
              resultsByJobId={modalData.results}
              onToggleExpand={modalData.toggleOrderExpansion}
              onEnsureJobsLoaded={async (orderId) => {
                console.log('🔄 Jobs already loaded for order:', orderId);
              }}
              onOpenOrder={handleNavigateToOrder}
              onOpenJob={handleNavigateToJob}
              hasMore={modalData.hasMore}
              isLoading={modalData.loading}
              onLoadMore={modalData.loadMore}
            />
          );
        }
    }
    
    return (
      <div className="empty-state">
        <p>No content available</p>
      </div>
    );
  };

  // Render compact, dynamic header based on navigation hierarchy
  const renderHeader = () => {
    
    return (
      <div className="unified-results-header">
        {/* Screen reader context */}
        <h1 id="analysis-results-title" className="sr-only">
          Analysis Results: {breadcrumbs.map(c => c.label).join(' › ')}
        </h1>
        
        {/* Header Content */}
        <div className="header-content">
          {/* Back Button - Show when viewing single job or order */}
          {(navigationState.mode === 'job' || navigationState.mode === 'order') && (
            <button
              className="header-back-button"
              onClick={handleNavigateToList}
              aria-label="Back to jobs list"
              title="Back to jobs list"
            >
              <FaArrowLeft />
            </button>
          )}
          
          {/* Title Container */}
          <div className="header-title">
            <h2>Analysis Results</h2>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="header-actions">
          {/* View Toggle Buttons - Only show in list view modes, not when viewing single job/order */}
          {navigationState.mode !== 'job' && navigationState.mode !== 'order' && (
            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'jobs' ? 'active' : ''}`}
                onClick={() => setViewMode('jobs')}
                title="Show jobs directly"
              >
                Jobs
              </button>
              <button
                className={`toggle-btn ${viewMode === 'orders' ? 'active' : ''}`}
                onClick={() => setViewMode('orders')}
                title="Show orders with jobs grouped inside"
              >
                Orders
              </button>
            </div>
          )}
          
          {/* Expand/Collapse Button (only in slide mode, not for evidence modals) */}
          {slideMode && origin !== 'evidence' && (
            <button 
              className="header-expand-button"
              onClick={() => {
                const newExpandedState = !isExpanded;
                setIsExpanded(newExpandedState);
                
                // If expanding and companion is open, close it
                if (newExpandedState && isCompanionExpanded) {
                  setIsCompanionExpanded(false);
                }
                
                // If collapsing modal, also close sidebar
                if (!newExpandedState && isSidebarOpen) {
                  setIsSidebarOpen(false);
                }
              }}
              aria-label={isExpanded ? "Collapse to 75% width" : "Expand to full width"}
              title={isExpanded ? "Collapse" : "Expand"}
              {...getInteractiveProps()}
            >
              {isExpanded ? '⇤' : '⇥'}
            </button>
          )}
          
          {/* Minimize Button */}
          <button 
            className="header-minimize-button"
            onClick={() => {
              setIsMinimized(!isMinimized);
              // Close sidebar when minimizing
              if (!isMinimized && isSidebarOpen) {
                setIsSidebarOpen(false);
              }
            }}
            aria-label={isMinimized ? "Restore modal" : "Minimize to footer"}
            title={isMinimized ? "Restore" : "Minimize"}
            {...getInteractiveProps()}
          >
            {isMinimized ? <FaChevronUp /> : <FaChevronDown />}
          </button>
          
          {/* Close Button */}
          <button 
            className="header-close-button"
            onClick={handleClose}
            aria-label="Close analysis results"
            {...getInteractiveProps()}
          >
            <FaTimes />
          </button>
        </div>
      </div>
    );
  };

  if (!isVisible) return null;

  // When minimized, render modal outside overlay
  if (isMinimized) {
    return (
      <ModalPortal>
        <div 
          ref={(el) => {
            modalRef.current = el;
            companionModalRef.current = el;
          }}
          className={`order-results-modal minimized`}
          style={{ zIndex }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Minimized Footer View */}
          <div className="minimized-footer">
            <div className="footer-content">
              <div className="footer-info">
                <span className="footer-title">Analysis Results</span>
                {evidenceName && (
                  <span className="footer-context">
                    {evidenceName}
                    {navigationState.mode === 'job' && (
                      <span className="footer-job-indicator"> • Job Analysis</span>
                    )}
                    {navigationState.mode === 'order' && (
                      <span className="footer-order-indicator"> • Order Analysis</span>
                    )}
                  </span>
                )}
              </div>
              <div className="footer-actions">
                <button 
                  className="footer-restore-button"
                  onClick={() => setIsMinimized(false)}
                  title="Restore modal"
                >
                  <FaChevronUp /> Restore
                </button>
                <button 
                  className="footer-close-button"
                  onClick={handleClose}
                  title="Close"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  const modalContent = (
    <>
      <div 
        className={`order-results-overlay ${slideMode ? 'slide-mode' : ''} ${!isTransitioning && isVisible ? 'open' : ''}`}
        style={{ zIndex }}
        onClick={handleClose}
        {...getModalProps()}
      >
        <div 
          ref={(el) => {
            modalRef.current = el;
            companionModalRef.current = el;
          }}
          className={`order-results-modal ${slideMode ? 'slide-mode' : ''} ${slideMode && isExpanded ? 'expanded' : ''} ${!isTransitioning && isVisible ? 'open' : ''} ${isCompanionExpanded ? 'companion-expanded' : ''} ${isSidebarOpen && !isMobile ? 'sidebar-expanded' : ''} ${origin === 'evidence' ? 'evidence-origin' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Integrated Sidebar - Positioned inside modal with left-edge tab */}
          <IntegratedSidebar
            isOpen={true} // Always available when modal is open
            onToggle={(expanded) => {
              setIsSidebarOpen(expanded);
              
              // Stagger the animations for smoother experience
              if (expanded) {
                // Delay modal expansion to let sidebar animate first
                setTimeout(() => {
                  setIsExpanded(true);
                }, 150); // Half of sidebar animation duration
              } else {
                // Collapse modal first, then sidebar
                setIsExpanded(false);
                // Sidebar will collapse after modal starts collapsing
              }
            }}
            currentView={navigationState.mode}
            context={sidebarContext}
            isMobile={isMobile}
            className="results-sidebar"
          />
          
          {/* Modal Main Content - Shifts when sidebar expands */}
          <div className={`modal-main-content ${isSidebarOpen ? 'sidebar-open' : ''}`}>
            {/* Dynamic Header */}
            {renderHeader()}

            {/* Content */}
            <div className="results-content">
          {currentData.loading && (viewMode === 'jobs' ? jobsData.jobs.length === 0 : modalData.orders.length === 0) ? (
            <div className="loading-state">
              <p>Loading analysis {viewMode === 'jobs' ? 'jobs' : 'results'}...</p>
            </div>
          ) : currentData.error ? (
            <div className="error-state">
              <p>Error loading {viewMode === 'jobs' ? 'jobs' : 'results'}: {typeof currentData.error === 'string' ? currentData.error : currentData.error.message || 'An unexpected error occurred'}</p>
              <button onClick={currentData.refresh}>Retry</button>
            </div>
          ) : (
            renderContent()
          )}
        </div>

            {/* Footer */}
            <div className="order-results-footer">
          <div className="footer-left">
            {/* Case and Evidence Context */}
            <div className="footer-context-info">
              {casefileName && (
                <>
                  <span className="footer-context-label">Case:</span>
                  <span className="footer-context-value">{casefileName}</span>
                </>
              )}
              {casefileName && evidenceName && (
                <span className="footer-context-separator"> › </span>
              )}
              {evidenceName && (
                <>
                  <span className="footer-context-label">Evidence:</span>
                  <span className="footer-context-value">{evidenceName}</span>
                </>
              )}
            </div>
              </div>
              <div className="footer-info footer-stats-mobile-hide">
                {(() => {
                  const { mode, jobId } = navigationState;
                  
                  if (mode === 'job' && jobId) {
                    // Check if we're in similar job navigation mode
                    if (isInSimilarJobMode && originalJobContext) {
                      const previousJob = jobNavigationHistory[jobNavigationHistory.length - 1];
                      
                      // Show dual navigation for similar job analysis
                      return (
                        <>
                          <div className="job-navigation-buttons">
                            {/* Previous Job Button (if history exists) */}
                            {previousJob && (
                              <button 
                                className="back-to-previous-job"
                                onClick={() => {
                                  console.log('⬅️ Going back to previous job:', previousJob);
                                  navigationService.navigateToJob(
                                    previousJob.jobId, 
                                    previousJob.orderId, 
                                    {
                                      updateUrl: true,
                                      source: 'back_to_previous_job'
                                    }
                                  );
                                }}
                                title={`Go back to previous job #${previousJob.jobId.slice(-8)}`}
                              >
                                ← Job #{previousJob.jobId.slice(-8)}
                              </button>
                            )}
                            
                            {/* Original Job Button */}
                            <button 
                              className="back-to-original-job"
                              onClick={() => {
                                console.log('🏠 Returning to original job:', originalJobContext);
                                console.log('🏠 Current job history:', jobNavigationHistory.map(j => j.jobId.slice(-8)));
                                navigationService.navigateToJob(
                                  originalJobContext.jobId, 
                                  originalJobContext.orderId, 
                                  {
                                    updateUrl: true,
                                    source: 'back_to_original_job'
                                  }
                                );
                              }}
                              title={`Return to original job #${originalJobContext.jobId.slice(-8)}`}
                            >
                              🏠 Job #{originalJobContext.jobId.slice(-8)}
                            </button>
                          </div>
                        </>
                      );
                    } else {
                      // Show job metadata for regular job view
                      const currentJob = jobsData.getJobById(jobId);
                      
                      if (currentJob) {
                        const jobDate = currentJob.createdAt?.toDate ? 
                          currentJob.createdAt.toDate().toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          }) : 'Unknown date';
                        
                        return (
                          <>
                            <span>{jobDate}</span>
                            <span className="info-separator">•</span>
                            <span>Job: {jobId.slice(-8)}</span>
                          </>
                        );
                      } else {
                        // Fallback: show basic job info
                        return (
                          <>
                            <span>Job: {jobId.slice(-8)}</span>
                            <span className="info-separator">•</span>
                            <span>Analysis View</span>
                          </>
                        );
                      }
                    }
                  }
                  
                  // Default view for list/order modes - no stats shown
                  return null;
                })()}
              </div>
            </div>
          </div>
          </div> {/* End of modal-main-content */}
          
        {/* Accessibility announcements */}
        <div className="sr-only" aria-live="polite" aria-atomic="false">
          {announcements.map((announcement, index) => (
            <div key={index}>{typeof announcement === 'string' ? announcement : JSON.stringify(announcement)}</div>
          ))}
        </div>
        
        {/* Companion Modal Integration - Preserved for future use (currently disabled) */}
        {/* <CompanionModalIntegration
          modalState={{
            viewType: navigationState.mode === 'job' ? 'single-job' : 
                     navigationState.mode === 'order' ? 'single-order' :
                     viewMode === 'jobs' ? 'job-list' : 'order-list',
            selectedItem: navigationState.jobId ? { jobId: navigationState.jobId } :
                         navigationState.orderId ? { orderId: navigationState.orderId } : null,
            evidenceData: {
              id: evidenceId,
              name: evidenceName || `Evidence ${evidenceId}`,
              jobs: jobsData.jobs,
              jobCount: jobsData.stats.total,
              orderCount: modalData.stats.orders.total
            },
            orders: modalData.orders
          }}
          parentModalRef={companionModalRef}
          onCompanionToggle={(companionIsExpanded) => {
            setIsCompanionExpanded(companionIsExpanded);
            
            // If companion is opening and results modal is expanded, collapse results modal
            if (companionIsExpanded && isExpanded) {
              setIsExpanded(false);
            }
          }}
          forceCollapsed={isExpanded} // Close companion when results modal expands
        /> */}
        </div>
    </>
  );

  return (
    <ModalErrorBoundary componentName="Unified Results Modal">
      {disablePortal ? modalContent : <ModalPortal>{modalContent}</ModalPortal>}
    </ModalErrorBoundary>
  );
};

export default UnifiedResultsModal;
